import { isLeft, toError } from "fp-ts/Either";
import { Errors, Validation } from "io-ts";
import logger from "./logging";
import { PathReporter } from "io-ts/PathReporter";
import { Left } from "fp-ts/lib/Either";
import { ERROR_INPUT_DECODING_FAILED, UnprocessableEntityError } from "../errors";

/**
 * Unwraps io-ts validation result or throws InputDecodingFailedError
 * @param validation The io-ts validation result
 * @returns The decoded value
 * @throws InputDecodingFailedError if validation fails
 */
export function unwrapDecoding<T>(validation: Validation<T>): T {
    if (isLeft(validation)) {
        const issues = extractIssues(validation.left);
        const decodingError = new InputDecodingFailedError(validation, issues);
        logger.error("Invalid decoding", {
            issues,
            issueSummary: decodingError.issueSummary,
            error: toError(validation.left),
            val: validation.left,
        });
        throw decodingError;
    }
    return validation.right;
}

/**
 * error for failed io-ts-decoding
 */
export class InputDecodingFailedError extends UnprocessableEntityError {
    readonly detailedMessage: string;
    readonly errors: Left<Errors>;
    readonly issues: DecodingIssue[];
    readonly issueSummary: string;

    constructor(params: Left<Errors>, issues?: DecodingIssue[]) {
        super("Decoding of input failed because of wrong format", ERROR_INPUT_DECODING_FAILED, {
            cause: new AggregateError(params.left),
        });
        this.detailedMessage = PathReporter.report(params).join("\n");
        this.errors = params;
        this.issues = issues ?? extractIssues(params.left);
        this.issueSummary = summarizeIssues(this.issues);
    }
}

// shape for structured decoding issues
type DecodingIssue = {
    path: string; // e.g. "user.address[0].street"
    pathParts: string[]; // e.g. ["user", "address", "0", "street"]
    expected: string; // e.g. "string"
    actual: unknown; // the offending value
    message?: string; // optional custom message if present
};

// turn io-ts Errors into DecodingIssue[]
function extractIssues(errors: Errors): DecodingIssue[] {
    try {
        return errors.map((err) => {
            const context = err.context ?? [];
            const pathParts = context
                .slice(1) // skip the root entry
                .map((ctx) => ctx.key)
                .filter((key) => key.length > 0);

            // build a JS-like path from the context
            const path =
                pathParts
                    .map((key) => (/^\d+$/.test(key) ? `[${key}]` : `.${key}`))
                    .join("")
                    .replace(/^\./, "") || "root";

            // the expected type name is on the last context entry
            const expected = context[context.length - 1]?.type.name ?? "unknown";

            return {
                path,
                pathParts,
                expected,
                actual: (err as any).value, // ValidationError has a value field
                message: (err as any).message,
            };
        });
    } catch (error) {
        logger.warn("Failed to extract decoding issues", { error });
        return [
            {
                path: "root",
                pathParts: [],
                expected: "unknown",
                actual: undefined,
                message: error instanceof Error ? error.message : "Unknown error while parsing validation issues",
            },
        ];
    }
}

function summarizeIssues(issues: DecodingIssue[]): string {
    if (!issues.length) {
        return "No validation errors";
    }

    return issues
        .map((issue) => {
            const location = issue.path || "root";
            const actual = formatActual(issue.actual);
            const base = `${location}: expected ${issue.expected}, got ${actual}`;
            return issue.message ? `${base} (${issue.message})` : base;
        })
        .join(";\n");
}

function formatActual(value: unknown): string {
    if (value === undefined) return "undefined";
    if (value === null) return "null";
    if (typeof value === "string") return `"${value}"`;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return "[object Object]";
        }
    }
    return String(value);
}
